import { makeActions } from 'utils/redux-tools';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export const REDUCER_KEY = 'overviewDashboardv2';

const getPreferenceSnapshot = state =>
    cloneDeep({
        hiddenColumns: state.hiddenColumns,
        sortBy: state.sortBy,
        assigneeFilter: state.assigneeFilter,
        organizationFilter: state.organizationFilter,
    });

const updatePreferenceState = (state, changes) => {
    const nextState = { ...state, ...changes };

    return {
        ...nextState,
        hasUnsavedPreferences: !isEqual(getPreferenceSnapshot(nextState), state.savedPreferences),
    };
};

const initialPreferences = {
    hiddenColumns: {
        live: [],
        ended: [],
        upcoming: [],
    },
    sortBy: {
        live: [],
        ended: [],
        upcoming: [],
    },
    assigneeFilter: {
        live: [],
        ended: [],
        upcoming: [],
    },
    organizationFilter: [],
};

export const initialState = {
    isLoading: false,
    hasLoadedUserPreference: false,
    hasUnsavedPreferences: false,
    isSavingPreferences: false,
    savedPreferences: initialPreferences,
    etag: null,
    endingSoon: [],
    upcoming: [],
    recentlyEnded: [],
    campaignsEndingSoon: [],
    campaignsUpcoming: [],
    campaignsEnded: [],
    alerts: [],
    hiddenColumns: {
        live: [],
        ended: [],
        upcoming: [],
    },
    sortBy: {
        live: [],
        ended: [],
        upcoming: [],
    },
    assigneeFilter: {
        live: [],
        ended: [],
        upcoming: [],
    },
    organizationFilter: [],
    ownOrganization: {},
    alertsSidebar: {
        alerts: {},
        isLoading: false,
        error: null,
    },
    selfServeOrgList: [],
};

export const { reducer, actions } = makeActions({
    reducerKey: REDUCER_KEY,
    initialState,
    reducers: {
        init: state => ({
            ...state,
            isLoading: true,
        }),
        initSuccess: (state, payload) => ({
            ...state,
            endingSoon: payload.endingSoon,
            upcoming: payload.upcoming,
            recentlyEnded: payload.recentlyEnded,
            campaignsEndingSoon: payload.campaignsEndingSoon,
            campaignsUpcoming: payload.campaignsUpcoming,
            campaignsEnded: payload.campaignsEnded,
            alerts: payload.alerts,
            ownOrganization: payload.ownOrganization,
            isLoading: false,
        }),
        initError: state => ({
            ...state,
            isLoading: false,
        }),
        fetchUserPreference: state => state,
        fetchUserPreferenceSuccess: (state, payload) => {
            const nextState = {
                ...state,
                hasLoadedUserPreference: true,
                hiddenColumns: payload.hiddenColumns || state.hiddenColumns,
                sortBy: payload.sortBy || state.sortBy,
                assigneeFilter: payload.assigneeFilter || state.assigneeFilter,
                organizationFilter: payload.organizationFilter || [],
                etag: payload.etag,
            };

            return {
                ...nextState,
                savedPreferences: getPreferenceSnapshot(nextState),
                hasUnsavedPreferences: false,
            };
        },
        fetchUserPreferenceError: state => ({
            ...state,
            hasLoadedUserPreference: true,
        }),
        updateHiddenColumns: (state, payload) =>
            updatePreferenceState(state, {
                hiddenColumns: {
                    ...state.hiddenColumns,
                    [payload.section]: payload.hiddenColumns,
                },
            }),
        updateHiddenColumnsSuccess: (state, payload) => ({
            ...state,
            hiddenColumns: payload.hiddenColumns,
        }),
        updateSortBy: (state, payload) =>
            updatePreferenceState(state, {
                sortBy: {
                    ...state.sortBy,
                    [payload.section]: payload.sortBy,
                },
            }),
        updateSortBySuccess: (state, payload) => ({
            ...state,
            sortBy: payload.sortBy,
        }),
        updateAssigneeFilter: (state, payload) =>
            updatePreferenceState(state, {
                assigneeFilter: {
                    ...state.assigneeFilter,
                    [payload.section]: payload.assigneeFilter,
                },
            }),
        updateOrganizationFilter: (state, payload) =>
            updatePreferenceState(state, {
                organizationFilter: payload.organizationFilter,
            }),
        saveUserPreference: state => ({
            ...state,
            isSavingPreferences: true,
        }),
        saveUserPreferenceSuccess: (state, payload) => {
            const savedPreferences = cloneDeep(payload.savedPreferences);

            return {
                ...state,
                etag: payload.etag || state.etag,
                savedPreferences,
                hasUnsavedPreferences: !isEqual(getPreferenceSnapshot(state), savedPreferences),
                isSavingPreferences: false,
            };
        },
        saveUserPreferenceError: state => ({
            ...state,
            hasUnsavedPreferences: true,
            isSavingPreferences: false,
        }),
        fetchSidebarAlerts: (state, payload) => ({
            ...state,
            alertsSidebar: {
                alerts: {
                    issue: payload.issue,
                    notice: payload.notice,
                    id: payload.id,
                    name: payload.name,
                    type: payload.type,
                },
                isLoading: true,
                error: null,
            },
        }),
        fetchSidebarAlertsSuccess: (state, payload) => ({
            ...state,
            alertsSidebar: {
                alerts: {
                    ...state.alertsSidebar.alerts,
                    alerts: payload,
                },
                isLoading: false,
                error: null,
            },
        }),
        fetchSidebarAlertsError: (state, payload) => ({
            ...state,
            alertsSidebar: {
                alerts: {},
                isLoading: false,
                error: payload,
            },
        }),
        fetchSelfServeOrgList: state => ({
            ...state,
            selfServeOrgList: [],
        }),
        fetchSelfServeOrgListSuccess: (state, payload) => ({
            ...state,
            selfServeOrgList: payload.selfServeOrgList,
        }),
    },
});
